import { UPDATE_CURRENT_HOTEL } from 'actions/actionTypes'
import { saveAccount } from 'features/Login/savedAccounts'
import dashboardApi from 'services/api/dashboard'
import userApi from 'services/api/user'

import { brandVariants } from '../main/hotelBrandsAndChains'
import LocalUserService from '../services/localUserService'
import { clearSubsidiary } from '../utils/helpers'

const userService = new LocalUserService()

export const setMagicLinkStatus = magicLinkStatus => ({
  type: 'NEW_MAGIC_LINK_STATUS',
  payload: magicLinkStatus
})

export const onSetAppState = payloadObject => ({
  type: 'UPDATE_APP_STATE',
  payload: payloadObject
})

export const loginUser = (currentUser, userToken, userId, sessionId) => {
  return async dispatch => {
    dispatch({
      type: 'TRYING_TO_LOG_IN'
    })

    try {
      const loginStatus = await dashboardApi.checkLogin()

      if (loginStatus === 'alive') {
        dispatch({
          type: 'USER_LOGGED_IN',
          payload: { currentUser, userToken, userId, sessionId }
        })
        if (currentUser?.email) {
          saveAccount({
            name: currentUser.fullName || currentUser.name,
            email: currentUser.email
          })
        }

        if (window?.electron?.postMessage) {
          window.electron.postMessage({
            type: 'token-updated',
            data: {
              spAuth: sessionId
            }
          })
        }
      } else {
        // If loginStatus is not 'alive' (undefined, null, or other value), treat as no login info
        dispatch({
          type: 'NO_LOGIN_INFO_FOUND'
        })
      }
    } catch (error) {
      // On error, also treat as no login info to set tryingLogIn to false
      dispatch({
        type: 'NO_LOGIN_INFO_FOUND'
      })
    }
  }
}

export const editSelf = ({ fullName = null, phone = null, email = null }) => {
  return async dispatch => {
    userApi
      .editSelf({ fullName, phone, email })
      .then(res => {
        dispatch({
          type: 'UPDATE_USER_INFO',
          payload: {
            fullName: res.fullName,
            phone: res.phone,
            email: res.email
          }
        })
      })
      .catch(err => {
        console.warn(err)
      })
  }
}

export const noLoginInfoFound = () => ({
  type: 'NO_LOGIN_INFO_FOUND'
})

export const turnOnInternalAdmin = () => ({
  type: 'IS_INTERNAL_ADMIN'
})

export const turnOffInternalAdmin = () => ({
  type: 'NOT_INTERNAL_ADMIN'
})

export const turnOffDemoMode = () => ({
  type: 'DEMO_MODE_OFF'
})

export const turnOnDemoMode = () => ({
  type: 'DEMO_MODE_ON'
})

export const logoutUser = () => {
  return async (dispatch, getState) => {
    const state = getState()

    const pms = state.login.subsidiaryPMS
    const subsidiaryId = state.login.currentHotelId
    if (window?.electron?.postMessage) {
      window.electron.postMessage({
        type: 'logged-out',
        data: {
          subsidiaryId,
          pms
        }
      })
    }

    await dashboardApi.logout()
    userService.removeCurrentUser()
    clearSubsidiary()

    dispatch({
      type: 'USER_LOGGED_OUT'
    })
  }
}

export const setSession = sessionId => ({
  type: 'SET_SESSION',
  payload: sessionId
})

function findMatchingBrand(subsidiaryName) {
  const match = brandVariants.find(b => {
    if (subsidiaryName) {
      return subsidiaryName.match(new RegExp(b, 'i'))
    }
    return false
  })
  return match
}

/**
 *
 * @param {String} subsidiaryName
 * @returns {{brand: String, shortName: String}}
 */
export function generateShortNameAndBrand(subsidiaryName) {
  const brand = findMatchingBrand(subsidiaryName)

  return {
    brand,
    shortName: brand
      ? subsidiaryName.replace(new RegExp(`${brand},?`), '').trim()
      : subsidiaryName
  }
}

export const getSubsidiaryList = () => {
  return async dispatch => {
    dispatch({
      type: 'GET_SUBSIDIARY_LIST_STARTED'
    })

    try {
      const response = await dashboardApi.getHotelListForMerchant()

      if (response.isInternalAdmin) {
        dispatch({
          type: 'INTERNAL_ADMIN_LOGGED_IN'
        })
      }

      const data = {
        ...response,
        subsidiaries: response.subsidiaries.map(s => ({
          ...s,
          ...generateShortNameAndBrand(s.subsidiaryName)
        }))
      }

      dispatch(saveSubsidiaryList(data))
    } catch (err) {
      const rawMessage = err?.message || String(err)
      const message = rawMessage.includes('No subsidiaries found')
        ? 'No hotels are linked to your account. Contact your administrator if you need access.'
        : rawMessage
      dispatch({
        type: 'GET_SUBSIDIARY_LIST_FAILED',
        payload: { message }
      })
    }
  }
}

export const postAndSelectSubsidiary = subsidiaryId => {
  return async dispatch => {
    dispatch({
      type: 'WILL_CHECK_LOGIN'
    })

    const login = await dashboardApi.checkLogin()

    if (login === 'alive') {
      dispatch({
        type: 'POST_AND_SELECT_SUBSIDIARY_STARTED'
      })

      try {
        const {
          roles,
          position,
          isInternalGlobalUser: isInternalAdmin,
          isCsmUser,
          csmUserId,
          isGroupLevelUser,
          isGdsBookingAvailable,
          isIndependentlyManaged,
          bookingEngine,
          bookingEngineSlug,
          parentChainCode,
          externalHotelId,
          primaryWebsiteUrl,
          currency,
          currentUtcOffset,
          ianaTimeZoneId,
          inviteBackIsDisabled,
          subsidiaryName,
          subsidiaryNameForText,
          merchantName,
          merchantId,
          pms,
          isPmsAutoSyncAdvised,
          hasBillingContact,
          hasTripAdvisorLink,
          locale,
          billingAddress,
          subsidiaryShortLinkToken,
          displayId,
          cultureCode,
          status,
          hasMissingCampaignRegisterData,
          activeServices,
          numberOfRooms,
          isPreOnboardingCompleted,
          isSetupCompleted,
          subsidiaryInfo: { csmEmail },
          hubspotId
        } = await dashboardApi.postSelectedHotelMerchant(subsidiaryId)

        dispatch({
          type: 'SAVE_POSTED_SUBSIDIARY',
          payload: {
            roles,
            position,
            isInternalAdmin,
            isCsmUser,
            csmUserId: isCsmUser ? csmUserId : null,
            subsidiaryId,
            bookingEngine,
            inviteBackIsDisabled,
            isIndependentlyManaged,
            bookingEngineSlug,
            parentChainCode,
            externalHotelId,
            primaryWebsiteUrl,
            currency,
            currentUtcOffset,
            ianaTimeZoneId,
            currentHotel: subsidiaryName,
            currentHotelShort: subsidiaryNameForText,
            currentHotelId: subsidiaryId,
            currentGroupName: merchantName,
            currentGroupId: merchantId,
            cultureCode,
            subsidiaryPMS: pms,
            currentPmsNeedSync: isPmsAutoSyncAdvised,
            shouldAskAddBillingContact: !hasBillingContact,
            hasTripAdvisorLink,
            hasBillingContact,
            locale: locale,
            billingAddress,
            subsidiaryShortLinkToken,
            displayId,
            isGdsBookingAvailable,
            status,
            hasMissingCampaignRegisterData,
            activeServices,
            numberOfRooms,
            isGroupLevelUser,
            isPreOnboardingCompleted,
            isSetupCompleted,
            csmEmail,
            hubspotId
          }
        })
        if (window?.electron?.postMessage) {
          const userData = userService.getCurrentUserInfo()

          window.electron.postMessage({
            type: 'logged-in',
            data: {
              spAuth: userData.sessionId,
              subsidiaryId,
              fullName: userData.user.name,
              userId: userData.userId,
              hotelName: subsidiaryName
            }
          })
        }
      } catch (err) {
        console.warn({ err })
        dispatch({
          type: 'POST_SUBSIDIARY_FAILED',
          payload: err
        })
      }
    } else {
      dispatch({
        type: 'SUBSIDIARY_SWITCH_DELAYED'
      })
    }
  }
}

export const saveSubsidiaryList = ({ subsidiaries, isInternalAdmin }) => ({
  type: 'SAVE_SUBSIDIARY_LIST',
  payload: {
    subsidiaries,
    isInternalAdmin
  }
})

export const qrLoggingIn = () => ({
  type: 'QR_LOGGING_IN'
})

export const updateMerchantDetails = merchantDetails => ({
  type: 'UPDATE_MERCHANT_DETAILS',
  payload: { merchantDetails }
})

export const updateCurrentHotel = subsidiaryName => ({
  type: UPDATE_CURRENT_HOTEL,
  payload: subsidiaryName
})

export const savePrevPath = path => {
  const excludedPaths = ['undefined', '/login', 'selectHotel']

  if (excludedPaths.some(excludedVal => path.includes(excludedVal))) {
    path = '/'
  }

  return {
    type: 'SAVE_PREV_PATH',
    payload: path
  }
}

export const tryingLogIn = boolean => ({
  type: 'SET_TRYING_LOG_IN',
  payload: boolean
})

export const rejectAddBillingContact = () => ({
  type: 'REJECT_ADD_BILLING_CONTACT'
})
